import { useState, useEffect } from 'react';
import { Scan, Save, Plug } from 'lucide-react';

export const ScannerTab = ({ showNotification }) => {
  const [enabled, setEnabled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    const savedScanner = localStorage.getItem('org.device.scanner');
    if (savedScanner) {
      try {
        const parsed = JSON.parse(savedScanner);
        if (parsed.enabled !== undefined) setEnabled(parsed.enabled);
      } catch (e) {
        console.error('Failed to parse saved scanner config', e);
      }
    }
    setIsLoaded(true);
  }, []);

  const handleSave = () => {
    localStorage.setItem('org.device.scanner', JSON.stringify({ enabled }));
    showNotification('Scanner configuration saved successfully.', 'success');
  };

  if (!isLoaded) return null;

  return (
    <div className="w-full h-full flex flex-col space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12 flex-1">
        <div className="w-full space-y-10">
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-slate-800">Scanner Setup</h3>
              <p className="text-sm text-slate-500 leading-relaxed max-w-2xl">
                Barcode scanners act exactly like a fast keyboard. Enable this feature to instantly capture barcode scans in the background while on the Billing screen—no need to click on a search box first!
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 mt-4">
              <p className="font-bold flex items-center gap-2 text-sm text-slate-800"><Plug size={16} /> How to connect a scanner?</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
                <li><strong>USB Wired:</strong> Simply plug the USB cable into your computer. No drivers needed.</li>
                <li><strong>Wireless USB Dongle:</strong> Plug the provided 2.4GHz USB receiver (dongle) into your computer. Turn the scanner on to pair automatically.</li>
                <li><strong>Bluetooth:</strong> Pair the scanner with your device using your computer's built-in Bluetooth settings.</li>
                <li><strong>Test it:</strong> Open any text editor (like Notepad), click inside, and scan a barcode. The scanner should type the code instantly!</li>
              </ul>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-6 rounded-xl border border-slate-200 bg-slate-50/50">
            <div className="flex-1 pr-8">
              <h4 className="text-base font-bold text-slate-900 mb-1">
                Global Background Scanning
              </h4>
              <p className="text-sm text-slate-500">
                When active, the Billing tab will listen for rapid consecutive keystrokes to instantly add items to your cart.
              </p>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={enabled} 
                onChange={(e) => setEnabled(e.target.checked)} 
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
            </label>
          </div>

          <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm font-medium text-slate-500">
              Current Status: <span className={`font-bold uppercase ${enabled ? 'text-[var(--theme-primary)]' : 'text-slate-400'}`}>{enabled ? 'Active' : 'Disabled'}</span>
            </div>
            <button
              onClick={handleSave}
              className="w-full sm:w-auto h-10 px-6 text-sm flex items-center justify-center bg-[#0d0d0d] text-white font-bold rounded-md hover:bg-black shadow-sm transition-all hover:scale-[1.02]"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
